import { NgModule } from '@angular/core';
import { RouterModule, Routes, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RulesExecutionSystemComponent } from './rules-execution-system/rules-execution-system.component';
import { PatientManagementSystemComponent } from './patient-management-system/patient-management-system.component';
import { PatientManagementSystemAggregatorComponent } from './patient-management-system-aggregator/patient-management-system-aggregator.component';
import { PatientSearchComponent } from './patient-search/patient-search.component';
import { AuthGuard } from '../core/guards/auth.guard';
import { ComplianceRulesComponent } from './compliance-rules/compliance-rules.component';
import { RulesetsComponent } from './rulesets/rulesets.component';
import { ReminderSetsComponent } from './reminder-sets/reminder-sets.component';
import { AdherenceProfilerComponent } from './adherence-profiler/adherence-profiler.component';
import { EquipmentComponent } from '../equipment/equipment.component';
import { DevicesComponent } from './devices/devices.component';
import { AccessoriesComponent } from './accessories/accessories.component';
import { OrganizationManagementComponent } from './organization-management/organization-management.component';
import { AddCustomerComponent } from './organization-management/add-customer/add-customer.component';
import { OrganizationSettingsComponent } from './organization-management/organization-settings/organization-settings.component';
import { AddSubOrganizationComponent } from './organization-management/add-sub-organization/add-sub-organization.component';
import { AddUserComponent } from './organization-management/user-management/add-user/add-user.component';
import { EditOrganizationComponent } from './organization-management/edit-organization/edit-organization.component';
import { EditSubOrganizationComponent } from './organization-management/edit-sub-organization/edit-sub-organization.component';
import { Injectable } from '@angular/core';
import { AuthService } from '../core/services/auth.service';
import { Router } from '@angular/router';
import { NotAuthorizedComponent } from './not-authorized.component';
import { EmptyComponent } from './empty.component';
import { SrcRulesComponent } from './src-rules/src-rules.component';
import { ShowUserComponent } from './organization-management/user-management/show-user/show-user.component';
import { EditUserComponent } from './organization-management/user-management/show-user/edit-user/edit-user.component';
import { ShowSubOrganizationComponent } from './organization-management/show-sub-organization/show-sub-organization.component';
import { ShowOrganizationComponent } from './organization-management/show-organization/show-organization.component';

@Injectable({ providedIn: 'root' })
class OrgPermissionGuard implements CanActivate {
  constructor(private auth: AuthService, private router: Router) {}
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    const required = route.data['requiredPermission'] as string | string[];

    // Check if this route requires an orgId parameter but doesn't have one
    if (route.routeConfig?.path?.includes(':orgId') && !route.params['orgId']) {
      const userTopOrgId = this.auth.userTopOrgId;
      if (userTopOrgId) {
        // Extract the base path without the parameter
        const basePath = route.routeConfig.path.replace('/:orgId', '');
        const parentUrlSegments = route.parent ?
        route.parent.url.map((u) => u.path) : [];
        return this.router.createUrlTree([...parentUrlSegments, basePath, userTopOrgId ]);
      }
    }

    // If this is the default (empty path) child route, redirect to the first accessible tab or not-authorized
    if (route.routeConfig && route.routeConfig.path === '') {
      const siblings = route.parent?.routeConfig?.children || [];
      const parentUrlSegments = route.parent ?
      route.parent.url.map((u) => u.path) : [];
      for (const sibling of siblings) {
        if (!sibling.path || sibling.path === '') continue; // skip self
        const siblingPermission = sibling.data?.requiredPermission;
        if ( !siblingPermission || this.auth.hasPermission(...(Array.isArray(siblingPermission) ? siblingPermission : [siblingPermission]))) {
          // If the sibling path has :orgId, create proper URL segments
          if (sibling.path?.includes(':orgId') && this.auth.userTopOrgId) {
            const basePath = sibling.path.replace('/:orgId', '');
            return this.router.createUrlTree([
              ...parentUrlSegments,
              basePath,
              this.auth.userTopOrgId,
            ]);
          }
          return this.router.createUrlTree([...parentUrlSegments, sibling.path]);
        }
      }
      return this.router.createUrlTree(['/not-authorized']);
    }

    if ( !required || this.auth.hasPermission(...(Array.isArray(required) ? required : [required]))) {
      return true;
    }

    // Try to find the first accessible child route (tab)
    const siblings = route.parent?.routeConfig?.children || [];
    const parentUrlSegments = route.parent ? route.parent.url.map((u) => u.path) : [];
    for (const sibling of siblings) {
      const siblingPermission = sibling.data?.requiredPermission;
      if ( !siblingPermission || this.auth.hasPermission(...(Array.isArray(siblingPermission) ? siblingPermission : [siblingPermission]))) {
        // If the sibling path has :orgId, create proper URL segments
        if (sibling.path?.includes(':orgId') && this.auth.userTopOrgId) {
          const basePath = sibling.path.replace('/:orgId', '');
          return this.router.createUrlTree([
            ...parentUrlSegments,
            basePath,
            this.auth.userTopOrgId,
          ]);
        }
        return this.router.createUrlTree([...parentUrlSegments, sibling.path]);
      }
    }

    // If no accessible tab, redirect to a global fallback (not-authorized or root)
    return this.router.createUrlTree(['/not-authorized']); // Change to '/' if you don't have a not-authorized page
  }
}

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard], // No AuthGuard here, as this is a child route of the dashboard
    component: PatientSearchComponent, // The one with the <dls-tabs> and <router-outlet>
    children: [
      { path: '', redirectTo: 'pms', pathMatch: 'full' },
      { path: 'pms', component: PatientManagementSystemComponent },
      { path: 'pms-aggregator', component: PatientManagementSystemAggregatorComponent }
    ],
  },
  {
    path: 'rules',
    component: RulesExecutionSystemComponent,
    children: [
      { path: '', redirectTo: 'adherenceprofiler', pathMatch: 'full' },
      { path: 'adherenceprofiler', component: AdherenceProfilerComponent },
      { path: 'compliance', component: ComplianceRulesComponent },
      { path: 'rulesets', component: RulesetsComponent },
      { path: 'reminder-sets', component: ReminderSetsComponent },
      { path: 'rules-builder', component: ReminderSetsComponent },
      { path: 'src-rules', component: SrcRulesComponent }
    ]
  },
  {
    path: 'equipment',
    component: EquipmentComponent,
    children: [
      { path: '', redirectTo: 'devices', pathMatch: 'full' },
      { path: 'devices', component: DevicesComponent },
      { path: 'accessories', component: AccessoriesComponent }
    ]
  },
  {
    path: 'organization',
    canActivate: [AuthGuard],
    component: OrganizationManagementComponent,
    children: [
      {
        path: '',
        canActivate: [OrgPermissionGuard],
        component: EmptyComponent,
      },
      {
        path: 'add-customer',
        component: AddCustomerComponent,
        data: { requiredPermission: 'AccessProductSupportConsole' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'show-organization/:id',
        component: ShowOrganizationComponent,
        data: { requiredPermission: 'AccessProductSupportConsole' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'edit-organization/:id',
        component: EditOrganizationComponent,
        data: { requiredPermission: 'AccessProductSupportConsole' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'add-sub-organization/:orgId',
        component: AddSubOrganizationComponent,
        data: { requiredPermission: 'ViewPayers' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'edit-sub-organization/:parentId/:id',
        component: EditSubOrganizationComponent,
        data: { requiredPermission: 'ViewPayers' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'show-sub-organization/:orgId',
        component: ShowSubOrganizationComponent,
        data: { requiredPermission: 'ViewPayers' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'org-settings/:orgId',
        component: OrganizationSettingsComponent,
        data: { requiredPermission: 'ViewPayers' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'add-user/:orgId',
        component: AddUserComponent,
        data: { requiredPermission: 'ViewPayers' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'show-user/:orgId',
        component: ShowUserComponent,
        data: { requiredPermission: 'ViewPayers' },
        canActivate: [OrgPermissionGuard],
      },
      {
        path: 'edit-user/:orgId/:userId',
        component: EditUserComponent,
        data: { requiredPermission: 'ViewPayers' },
        canActivate: [OrgPermissionGuard],
      },
    ],
  },
  {
    path: 'not-authorized',
    component: NotAuthorizedComponent
  }
];
@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
  providers: []
})
export class ComponentsRoutingModule {}
